import { Typography as Text } from '@material-ui/core';

import { RiFileLine } from '@remixicon/react';

import { Link } from '@backstage/core-components';

import {
  extractLastNameComponent,
  useDocumentNavigation,
} from '@spotify/confidence-core-react';
import { useCollaborativeDocumentRef } from '@spotify/confidence-editor-react';
import { useExternalDocumentRef } from '@spotify/confidence-external-documents-react';
import {
  type ToolGroupEntry,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';
import { UNTITLED_DOCUMENT_TITLE } from '@spotify/plugin-confidence-collaboration-react';

import { confidenceChatPlugin } from '../plugin';

type DocumentType = 'collaborative' | 'external' | 'unknown';

function getDocumentType(name: string | undefined): DocumentType {
  if (name?.startsWith('collaborativeDocuments/')) return 'collaborative';
  if (name?.startsWith('externalDocuments/')) return 'external';
  return 'unknown';
}

const DOCUMENT_TOOL_SUFFIXES = [
  'read_document',
  'write_document',
  'edit_document',
];

function getDocumentName(input: unknown): string | undefined {
  if (input && typeof input === 'object' && 'name' in input) {
    const name = (input as { name?: string }).name;
    return typeof name === 'string' && name.length > 0 ? name : undefined;
  }
  return undefined;
}

function getInputTitle(input: unknown): string | undefined {
  if (input && typeof input === 'object' && 'title' in input) {
    const title = (input as { title?: string }).title;
    return typeof title === 'string' && title.length > 0 ? title : undefined;
  }
  return undefined;
}

function getActionVerb(
  tool: string,
  { completed }: { completed: boolean },
): string {
  if (tool.endsWith('read_document')) {
    return completed ? 'Read' : 'Reading';
  }
  if (tool.endsWith('edit_document')) {
    return completed ? 'Edited' : 'Editing';
  }
  return completed ? 'Wrote' : 'Writing';
}

export const DocumentToolLabel = ({ entry }: { entry: ToolGroupEntry }) => {
  const documentName = getDocumentName(entry.input);
  const documentType = getDocumentType(documentName);

  const { collaborativeDocument } = useCollaborativeDocumentRef({
    name: documentName ?? '',
    skip: documentType !== 'collaborative',
  });
  const { externalDocument } = useExternalDocumentRef({
    name: documentName ?? '',
    skip: documentType !== 'external',
  });
  const { buildDocumentHref } = useDocumentNavigation();

  const completed = Boolean(entry.result);
  const verb = getActionVerb(entry.tool, { completed });
  const title =
    collaborativeDocument?.title ??
    externalDocument?.title ??
    getInputTitle(entry.input) ??
    UNTITLED_DOCUMENT_TITLE;

  const documentId =
    documentType === 'collaborative'
      ? extractLastNameComponent(documentName)
      : undefined;
  const href = documentId ? buildDocumentHref(documentId) : undefined;

  return (
    <Text variant="body2" color="textSecondary">
      {verb}{' '}
      {href ? (
        <Link to={href} style={{ color: 'inherit' }}>
          {title}
        </Link>
      ) : (
        title
      )}
    </Text>
  );
};

export const DocumentToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'document-operation',
    predicate: tool =>
      DOCUMENT_TOOL_SUFFIXES.some(suffix => tool.endsWith(suffix)),
    icon: RiFileLine,
    display: 'grouped',
    renderLabel: entry => <DocumentToolLabel entry={entry} />,
    render: () => null,
  }),
);
